// Template Migration Service for vetscribe-amplify-react
// This service migrates existing templates from Amplify to Supabase with throttling

import { generateClient } from 'aws-amplify/api';
import { GraphQLQuery } from "@aws-amplify/api";
import { fetchUserAttributes, getCurrentUser } from "aws-amplify/auth";
import { templatesByLIDAndUpdatedAt } from '@/graphql/queries';
import { getTemplateShallow } from '@/graphql/getTemplateShallow';
import { DatabaseMigrationAdapter } from './DatabaseMigrationAdapter';
import {
  TemplatesByLIDAndUpdatedAtQuery,
  TemplatesByLIDAndUpdatedAtQueryVariables,
  GetTemplateQuery,
  ModelSortDirection,
  Template
} from '@/API';

interface MigrationProgress {
  total: number;
  processed: number;
  successful: number;
  failed: number;
  skipped: number;
  errors: string[];
}

interface MigrationConfig {
  batchSize: number;
  delayBetweenBatches: number; // milliseconds
  maxRetries: number;
  locationId?: string;
  dryRun: boolean;
}

interface MigrationResult {
  success: boolean;
  skipped?: boolean;
  reason?: string;
  error?: string;
  templateId?: string;
  name?: string;
}

export class TemplateMigrationService {
  private static instance: TemplateMigrationService;
  private databaseMigrationAdapter: DatabaseMigrationAdapter;
  private client = generateClient();
  private batchSize = 3; // Process 3 templates at a time
  private maxRetries = 2; // Retry failed operations up to 2 times
  private retryDelay = 5000; // 5 seconds delay between retries
  private failedOperations: Array<{
    templateData: any;
    error: string;
    timestamp: Date;
    retryCount: number;
  }> = [];
  private progress = {
    total: 0,
    processed: 0,
    successful: 0,
    failed: 0,
    skipped: 0,
    errors: [] as string[]
  };
  private defaultConfig = {
    batchSize: 3,
    maxRetries: 2,
    retryDelay: 5000
  };

  private constructor() {
    this.databaseMigrationAdapter = DatabaseMigrationAdapter.getInstance();
  }

  public static getInstance(): TemplateMigrationService {
    if (!TemplateMigrationService.instance) {
      TemplateMigrationService.instance = new TemplateMigrationService();
    }
    return TemplateMigrationService.instance;
  }

  /**
   * Migrate all templates for a location with throttling
   */
  async migrateAllTemplates(config: Partial<MigrationConfig> = {}): Promise<MigrationProgress> {
    const finalConfig = { ...this.defaultConfig, ...config };
    
    console.log('[TEMPLATE-MIGRATION] Starting migration with config:', finalConfig);
    
    try {
      // Reset progress
      this.resetProgress();

      // STEP 1: Ensure user exists in Supabase before migrating templates
      console.log('[TEMPLATE-MIGRATION] Step 1: Ensuring user exists in Supabase...');
      await this.ensureUserExists();

      // Get user location if not provided
      const locationId = finalConfig.locationId || await this.getCurrentUserLocation();
      
      // Fetch all templates
      const templates = await this.fetchAllTemplates(locationId);
      this.progress.total = templates.length;
      
      console.log(`[TEMPLATE-MIGRATION] Found ${templates.length} templates to migrate`);
      
      if (finalConfig.dryRun) {
        console.log('[TEMPLATE-MIGRATION] DRY RUN - No actual migration will occur');
        return this.analyzeTemplates(templates);
      }

      // Process templates in batches with throttling
      await this.processBatches(templates, finalConfig);
      
      console.log('[TEMPLATE-MIGRATION] Migration completed:', this.progress);
      return this.progress;

    } catch (error) {
      console.error('[TEMPLATE-MIGRATION] Migration failed:', error);
      this.progress.errors.push(`Migration failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      throw error;
    }
  }

  /**
   * Migrate a specific template by ID
   */
  async migrateTemplate(templateId: string, config: Partial<MigrationConfig> = {}): Promise<boolean> {
    const finalConfig = { ...this.defaultConfig, ...config };
    
    console.log(`[TEMPLATE-MIGRATION] Migrating single template: ${templateId}`);
    
    try {
      // Ensure user exists in Supabase before migrating template
      console.log('[TEMPLATE-MIGRATION] Ensuring user exists in Supabase...');
      await this.ensureUserExists();

      const template = await this.fetchTemplateWithDetails(templateId);
      if (!template) {
        console.warn(`[TEMPLATE-MIGRATION] Template ${templateId} not found`);
        return false;
      }

      if (finalConfig.dryRun) {
        console.log('[TEMPLATE-MIGRATION] DRY RUN - Template analysis:', template);
        return true;
      }

      const result = await this.migrateTemplateWithDetails(template);
      return result.success;

    } catch (error) {
      console.error(`[TEMPLATE-MIGRATION] Failed to migrate template ${templateId}:`, error);
      return false;
    }
  }

  /**
   * Migrate templates for specific location (used by runLocationTemplateMigration in script)
   */
  async migrateLocationTemplates(locationId: string, config: Partial<MigrationConfig> = {}): Promise<MigrationProgress> {
    console.log(`[TEMPLATE-MIGRATION] Migrating templates for location: ${locationId}`);
    
    try {
      // Ensure user exists in Supabase before migrating templates
      console.log('[TEMPLATE-MIGRATION] Ensuring user exists in Supabase...');
      await this.ensureUserExists();

      // Use the main migration method with location override
      return await this.migrateAllTemplates({ ...config, locationId });
    } catch (error) {
      console.error('[TEMPLATE-MIGRATION] Location template migration failed:', error);
      throw error;
    }
  }

  /**
   * Fetch all templates for a location
   */
  private async fetchAllTemplates(locationId: string): Promise<Template[]> {
    console.log(`[TEMPLATE-MIGRATION] Fetching templates for location: ${locationId}`);
    
    const templates: Template[] = [];
    let nextToken: string | undefined;

    do {
      const queryInput: TemplatesByLIDAndUpdatedAtQueryVariables = {
        lID: locationId,
        sortDirection: ModelSortDirection.DESC,
        limit: 100,
        nextToken,
        filter: {
          _deleted: { ne: true },
          archived: { ne: true },
          advanced: { ne: true }
        }
      };

      const response = await this.client.graphql<GraphQLQuery<TemplatesByLIDAndUpdatedAtQuery>>({
        query: templatesByLIDAndUpdatedAt,
        variables: queryInput
      });

      const items = response.data.templatesByLIDAndUpdatedAt?.items?.filter((item): item is Template => item !== null) || [];
      templates.push(...items);
      
      nextToken = response.data.templatesByLIDAndUpdatedAt?.nextToken || undefined;
      
      console.log(`[TEMPLATE-MIGRATION] Fetched ${items.length} templates, total so far: ${templates.length}`);
      
    } while (nextToken);

    return templates;
  }

  /**
   * Fetch a template with all its details
   */
  private async fetchTemplateWithDetails(templateId: string): Promise<Template | null> {
    console.log(`[TEMPLATE-MIGRATION] Fetching details for template: ${templateId}`);
    
    try {
      const response = await this.client.graphql<GraphQLQuery<GetTemplateQuery>>({
        query: getTemplateShallow,
        variables: { id: templateId }
      });

      const template = response.data.getTemplate as Template | null;
      
      // Exclude advanced templates from migration
      if (template?.advanced === true) {
        console.log(`[TEMPLATE-MIGRATION] Skipping advanced template: ${templateId}`);
        return null;
      }

      return template;
    } catch (error) {
      console.error(`[TEMPLATE-MIGRATION] Error fetching template details:`, error);
      
      // Log GraphQL errors in detail
      if (error && typeof error === 'object' && 'errors' in error) {
        const graphqlError = error as { errors: Array<{ message: string; errorType?: string }> };
        console.error(`[TEMPLATE-MIGRATION] GraphQL errors:`, graphqlError.errors);
        graphqlError.errors.forEach((err, index) => {
          console.error(`[TEMPLATE-MIGRATION] GraphQL error ${index + 1}:`, err.message, err.errorType);
        });
      }
      
      throw error;
    }
  }

  /**
   * Process templates in batches with throttling
   */
  private async processBatches(templates: Template[], config: MigrationConfig): Promise<void> {
    const batches = this.chunkArray(templates, config.batchSize);
    
    console.log(`[TEMPLATE-MIGRATION] Processing ${batches.length} batches of ${config.batchSize} templates each`);

    for (let i = 0; i < batches.length; i++) {
      const batch = batches[i];
      console.log(`[TEMPLATE-MIGRATION] Processing batch ${i + 1}/${batches.length}`);

      // Process batch in parallel
      const batchPromises = batch.map(template => this.migrateTemplateWithRetry(template, config.maxRetries));
      await Promise.allSettled(batchPromises);

      // Throttling: wait between batches (except for the last one)
      if (i < batches.length - 1) {
        console.log(`[TEMPLATE-MIGRATION] Waiting ${config.delayBetweenBatches}ms before next batch...`);
        await this.delay(config.delayBetweenBatches);
      }
    }
  }

  /**
   * Migrate a single template with retry logic
   */
  private async migrateTemplateWithRetry(template: Template, maxRetries: number): Promise<void> {
    let attempt = 0;
    
    while (attempt <= maxRetries) {
      try {
        // Fetch detailed template data if needed
        const detailedTemplate = template.sections?.items 
          ? template 
          : await this.fetchTemplateWithDetails(template.id);

        if (!detailedTemplate) {
          throw new Error('Failed to fetch template details');
        }

        const result = await this.migrateTemplateWithDetails(detailedTemplate);
        
        if (result.success) {
          this.progress.successful++;
          console.log(`[TEMPLATE-MIGRATION]  Successfully migrated template: ${template.name}`);
          return;
        } else if (result.skipped) {
          this.progress.skipped++;
          console.log(`[TEMPLATE-MIGRATION]  Skipped template: ${template.name} - ${result.reason}`);
          return;
        } else {
          throw new Error(result.error || 'Migration failed');
        }
      } catch (error) {
        attempt++;
        const errorMsg = error instanceof Error ? error.message : 'Unknown error';
        
        if (attempt <= maxRetries) {
          console.warn(`[TEMPLATE-MIGRATION]  Attempt ${attempt} failed for template ${template.name}, retrying... Error: ${errorMsg}`);
          await this.delay(1000 * attempt); // Exponential backoff
        } else {
          this.progress.failed++;
          this.progress.errors.push(`Template ${template.name}: ${errorMsg}`);
          console.error(`[TEMPLATE-MIGRATION]  Failed to migrate template after ${maxRetries} retries: ${template.name}`);
        }
      } finally {
        this.progress.processed++;
      }
    }
  }

  /**
   * Migrate template - uses the existing handleTemplateOperation method
   */
  private async migrateTemplateWithDetails(template: Template): Promise<MigrationResult> {
    console.log(`[TEMPLATE-MIGRATION] Migrating template: ${template.name}`);
    
    try {
             // Use the existing template operation handler from DatabaseMigrationAdapter
       const templateResult = await this.databaseMigrationAdapter.handleTemplateOperation('CREATE', template, undefined);
      
      if (!templateResult.success) {
        return {
          success: false,
          error: templateResult.error || 'Template migration failed'
        };
      }

      return {
        success: true,
        templateId: template.id,
        name: template.name
      };

    } catch (error) {
      console.error(`[TEMPLATE-MIGRATION] Error migrating template ${template.name}:`, error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Analyze templates for dry run
   */
  private async analyzeTemplates(templates: Template[]): Promise<MigrationProgress> {
    console.log('[TEMPLATE-MIGRATION] Analyzing templates for dry run...');
    
    for (const template of templates) {
      try {
        // Check if template is advanced
        if (template.advanced === true) {
          this.progress.skipped++;
          console.log(`[TEMPLATE-MIGRATION] Would skip: ${template.name} (advanced template)`);
          continue;
        }

        // Use the same logic as DatabaseMigrationAdapter for checking template type
        const templateType = String((template as unknown as Record<string, unknown>).type || 
                                   (template as unknown as Record<string, unknown>).templateType || '').toLowerCase();
        if (templateType && templateType !== 'standard') {
          this.progress.skipped++;
          console.log(`[TEMPLATE-MIGRATION] Would skip: ${template.name} (type: ${templateType})`);
        } else {
          this.progress.successful++;
          console.log(`[TEMPLATE-MIGRATION] Would migrate: ${template.name}`);
        }
        this.progress.processed++;
      } catch (error) {
        this.progress.failed++;
        this.progress.errors.push(`Template ${template.name}: Analysis failed`);
      }
    }

    return this.progress;
  }

  /**
   * Get current user's location
   */
  private async getCurrentUserLocation(): Promise<string> {
    const userAttr = await fetchUserAttributes();
    const locationId = userAttr["custom:location"];
    
    if (!locationId) {
      throw new Error('User location not found in attributes');
    }
    
    return locationId;
  }

  /**
   * Ensure user exists in Supabase before migrating templates
   * This prevents foreign key constraint violations
   */
  private async ensureUserExists(): Promise<void> {
    console.log('[TEMPLATE-MIGRATION] Checking if user exists in Supabase...');
    
    try {
      // Get current user info from Amplify
      const userAttr = await fetchUserAttributes();
      const amplifyUser = await getCurrentUser();
      
      // Prepare user data for migration
      // NOTE: Do NOT include 'id' field - let the Edge Function handle user creation and ID assignment
      const userData = {
        email: userAttr.email || '',
        name: userAttr.name || 
          (userAttr.given_name && userAttr.family_name 
            ? `${userAttr.given_name} ${userAttr.family_name}` 
            : userAttr.given_name || userAttr.family_name || 'Unknown User'),
        first_name: userAttr.given_name,
        last_name: userAttr.family_name,
        phone: userAttr.phone_number,
        location_id: userAttr["custom:location"],
        // Store original Amplify ID for reference
        original_amplify_id: amplifyUser.userId || amplifyUser.username,
        // Include usage and preference defaults for new users
        transcriptionTime: 0,
        transcriptionCreated: 0,
        currentTranscriptionsCreated: 0,
        audioRetentionPeriod: 30
      };

      console.log('[TEMPLATE-MIGRATION] Creating/updating user in Supabase:', { 
        email: userData.email,
        name: userData.name,
        original_amplify_id: userData.original_amplify_id
      });

      // Use the DatabaseMigrationAdapter to create/upsert the user
      console.log('[TEMPLATE-MIGRATION] Calling Edge Function to create/upsert user...');
      const userResult = await this.databaseMigrationAdapter.handleUserOperation('CREATE', userData);
      
      console.log('[TEMPLATE-MIGRATION] User operation result:', {
        success: userResult.success,
        skipped: userResult.skipped,
        error: userResult.error
      });
      
      if (userResult.success) {
        console.log('[TEMPLATE-MIGRATION] User exists/created successfully in Supabase');
        
        // Add a small delay to ensure user is fully committed before template migration
        console.log('[TEMPLATE-MIGRATION] Waiting 2 seconds for user creation to propagate...');
        await new Promise(resolve => setTimeout(resolve, 2000));
      } else if (userResult.skipped) {
        console.log('[TEMPLATE-MIGRATION] User already exists, skipping creation');
      } else {
        console.error('[TEMPLATE-MIGRATION] User creation failed:', userResult);
        
        // Check for specific foreign key errors
        if (userResult.error?.includes('profiles_id_fkey') || userResult.error?.includes('null value in column "id"')) {
          throw new Error(`User creation failed with database constraint error. This usually indicates the user auth setup failed. Please contact support or try logging out and back in. Error: ${userResult.error}`);
        } else {
          throw new Error(`User creation failed: ${userResult.error || 'Unknown error'}`);
        }
      }
    } catch (error) {
      console.error('[TEMPLATE-MIGRATION] Failed to ensure user exists:', error);
      throw new Error(`User validation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Utility methods
   */
  private chunkArray<T>(array: T[], chunkSize: number): T[][] {
    const chunks: T[][] = [];
    for (let i = 0; i < array.length; i += chunkSize) {
      chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private resetProgress(): void {
    this.progress = {
      total: 0,
      processed: 0,
      successful: 0,
      failed: 0,
      skipped: 0,
      errors: []
    };
  }

  /**
   * Get current migration progress
   */
  getProgress(): MigrationProgress {
    return { ...this.progress };
  }

  /**
   * Retry failed operations after a delay
   */
  private async retryFailedOperations(): Promise<void> {
    if (this.failedOperations.length === 0) return;

    console.log(`[TEMPLATE-MIGRATION] Retrying ${this.failedOperations.length} failed operations after 5 seconds...`);
    
    // Wait 5 seconds before retrying
    await new Promise(resolve => setTimeout(resolve, this.retryDelay));
    
    const operationsToRetry = [...this.failedOperations];
    this.failedOperations = []; // Clear the failed operations list
    
    for (const operation of operationsToRetry) {
      if (operation.retryCount >= this.maxRetries) {
        console.error(`[TEMPLATE-MIGRATION] Max retries reached for template: ${operation.templateData.name}`, operation.error);
        continue;
      }
      
      try {
        console.log(`[TEMPLATE-MIGRATION] Retrying template: ${operation.templateData.name} (attempt ${operation.retryCount + 1})`);
        await this.migrateTemplateWithDetails(operation.templateData);
        console.log(`[TEMPLATE-MIGRATION] Successfully retried template: ${operation.templateData.name}`);
      } catch (error) {
        console.error(`[TEMPLATE-MIGRATION] Retry failed for template: ${operation.templateData.name}`, error);
        
        // Add back to failed operations with incremented retry count
        this.failedOperations.push({
          ...operation,
          retryCount: operation.retryCount + 1,
          error: error.message || 'Unknown error',
          timestamp: new Date()
        });
      }
    }
    
    // If there are still failed operations, schedule another retry
    if (this.failedOperations.length > 0) {
      setTimeout(() => this.retryFailedOperations(), this.retryDelay);
    }
  }

  /**
   * Add a failed operation to the retry queue
   */
  private addFailedOperation(templateData: any, error: string): void {
    this.failedOperations.push({
      templateData,
      error,
      timestamp: new Date(),
      retryCount: 0
    });
  }

  /**
   * Get current status of failed operations
   */
  public getFailedOperationsStatus(): { count: number; operations: Array<{ name: string; error: string; retryCount: number }> } {
    return {
      count: this.failedOperations.length,
      operations: this.failedOperations.map(op => ({
        name: op.templateData.name || 'Unknown Template',
        error: op.error,
        retryCount: op.retryCount
      }))
    };
  }
}

// Export singleton instance
export const templateMigrationService = TemplateMigrationService.getInstance(); 